import type { TFunction } from "i18next";
import { SavedReportListDto } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { base64Decode, base64Encode } from "../utils/base64";
import type { SubAccess } from "../subscription/useSub";
import { savedReportQuery, type StoredReport } from "./expiringThisYearReport";

const LIST_PATHS: Record<SavedReportListDto, string> = {
  [SavedReportListDto.Contracts]: urls.app.contracts.viewAll,
  [SavedReportListDto.Procurements]: urls.app.sourcingEvents.viewAll,
  [SavedReportListDto.ContractTasks]: urls.app.contracts.tasks,
};

export const savedReportListPath = (list: SavedReportListDto): string => LIST_PATHS[list];

export const OPEN_REPORT_PARAM = "report";

export const savedReportLink = (report: StoredReport & { id: string }): string => {
  const params = new URLSearchParams(savedReportQuery(report));
  params.set(OPEN_REPORT_PARAM, report.id);
  return `${LIST_PATHS[report.list]}?${params.toString()}`;
};

const PAGING = "skip";
const ENCODED_FILTERS = "q";

export const withEncodedFilters = <Q>(query: string, rewrite: (q: Q) => Q): string => {
  const params = new URLSearchParams(query);
  const encoded = params.get(ENCODED_FILTERS);
  if (!encoded) return query;
  try {
    const q = JSON.parse(base64Decode(encoded)) as Q;
    const next = rewrite(q);
    if (next === q) return query;
    params.set(ENCODED_FILTERS, base64Encode(JSON.stringify(next)));
    return params.toString();
  } catch {
    return query;
  }
};
const COLUMNS = "columns";

const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key, entry]) => key !== PAGING && entry !== null && entry !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, entry]) => [key, canonical(entry)]),
  );
};

export const storedReportQuery = (query: string): string => {
  const params = new URLSearchParams(withEncodedFilters<unknown>(query.replace(/^\?/, ""), canonical));
  params.delete(OPEN_REPORT_PARAM);
  params.delete(PAGING);
  params.sort();
  return params.toString();
};

export type ReportQueryComparison = (query: string) => string;

export const isSameReportQuery = (
  saved: string,
  current: string,
  comparable: ReportQueryComparison = (query) => query
): boolean => {
  const savedParams = new URLSearchParams(storedReportQuery(comparable(saved)));
  const currentParams = new URLSearchParams(storedReportQuery(comparable(current)));
  if (!savedParams.has(COLUMNS)) currentParams.delete(COLUMNS);
  return savedParams.toString() === currentParams.toString();
};

export const savedReportListAllLabel = (t: TFunction, list: SavedReportListDto): string =>
  ({
    [SavedReportListDto.Contracts]: t("All contracts"),
    [SavedReportListDto.Procurements]: t("All procurements"),
    [SavedReportListDto.ContractTasks]: t("All contract tasks"),
  })[list];

export const savedReportListLabel = (t: TFunction, list: SavedReportListDto): string =>
  ({
    [SavedReportListDto.Contracts]: t("Contracts"),
    [SavedReportListDto.Procurements]: t("Procurements"),
    [SavedReportListDto.ContractTasks]: t("Contract tasks"),
  })[list];

export const canOpenSavedReportList = (access: SubAccess, list: SavedReportListDto): boolean =>
  list === SavedReportListDto.Procurements
    ? access.canViewSourcingEvents
    : access.canViewContracts || access.viewsSharedContracts;
